import dayjs from "dayjs";
import { Calendar as CalendarIcon, Gift, Info, Mail, Send, User } from "lucide-react";
import { BackButton } from "@/v2/components/BackButton";
import { PageHeader } from "@/v2/components/PageHeader";
import { Input } from "@/v2/components/ui/input";
import { Textarea } from "@/v2/components/ui/textarea";
import { RadioOptionCard } from "@/v2/components/ui/radio-option-card";

export function StepRecipientDelivery({
  formik,
  recipientHasSub,
  planPrice,
  pricesReady,
  onEmailChange,
  onEmailBlur,
  onBack,
}: {
  formik: any;
  recipientHasSub: boolean;
  planPrice: number;
  pricesReady: boolean;
  onEmailChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onEmailBlur: (e: React.FocusEvent<HTMLInputElement>) => void;
  onBack: () => void;
}) {
  return (
    <div className="space-y-6">
      <div>
        <BackButton onClick={onBack} />
        <PageHeader
          title="Recipient & Delivery"
          subtitle="Tell us who the gift is for and when you'd like it delivered. We'll send them an email with all the details."
          subtitleClassName="max-w-xl"
        />
      </div>

      <div className="bg-card border border-border rounded-2xl p-6 sm:p-8 space-y-6 shadow-xs">
        {/* RECIPIENT INFORMATION */}
        <div>
          <p className="text-xs font-bold uppercase tracking-wider mb-3 block text-muted-foreground">
            RECIPIENT INFORMATION
          </p>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
            <div>
              <label
                htmlFor="name"
                className="text-xs font-semibold mb-1.5 block text-foreground"
              >
                Recipient's name
              </label>
              <div className="relative">
                <User className="size-4 text-muted-foreground absolute left-3 top-2.5" />
                <Input
                  id="name"
                  name="name"
                  placeholder="Enter full name"
                  className="pl-9"
                  value={formik.values.name}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
              </div>
              {formik.touched.name && formik.errors.name && (
                <p className="text-xs text-destructive mt-1 font-medium">
                  {formik.errors.name}
                </p>
              )}
            </div>

            <div>
              <label
                htmlFor="email"
                className="text-xs font-semibold mb-1.5 block text-foreground"
              >
                Recipient's email
              </label>
              <div className="relative">
                <Mail className="size-4 text-muted-foreground absolute left-3 top-2.5" />
                <Input
                  id="email"
                  name="email"
                  type="email"
                  placeholder="Enter email address"
                  className="pl-9"
                  value={formik.values.email}
                  onChange={onEmailChange}
                  onBlur={onEmailBlur}
                />
              </div>
              {formik.touched.email && formik.errors.email && (
                <p className="text-xs text-destructive mt-1 font-medium">
                  {formik.errors.email}
                </p>
              )}
            </div>
          </div>

          {recipientHasSub && (
            <div className="rounded-lg bg-accent border border-accent-foreground/15 p-3.5 text-xs text-accent-foreground font-medium mb-4 flex items-start gap-2">
              <Info className="size-4 text-accent-foreground shrink-0 mt-0.5" />
              <span>
                This person already has an active subscription, so
                {pricesReady && planPrice > 0
                  ? ` the $${planPrice.toFixed(2)} gift amount`
                  : " your gift"}{" "}
                will be credited to their account and applied to their future
                invoices.
              </span>
            </div>
          )}

          <div>
            <div className="flex justify-between items-center mb-1.5">
              <label
                htmlFor="message"
                className="text-xs font-semibold block text-foreground"
              >
                Personal message
              </label>
              <span className="text-xs text-muted-foreground font-medium">
                {formik.values.message.length} / 500 characters
              </span>
            </div>
            <Textarea
              id="message"
              name="message"
              rows={3}
              maxLength={500}
              placeholder="Add a personal message to your gift..."
              value={formik.values.message}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
            {formik.touched.message && formik.errors.message && (
              <p className="text-xs text-destructive mt-1 font-medium">
                {formik.errors.message}
              </p>
            )}
          </div>
        </div>

        {/* DELIVERY OPTIONS */}
        <div>
          <p className="text-xs font-bold uppercase tracking-wider mb-3 block text-muted-foreground">
            DELIVERY OPTIONS
          </p>

          {/* flex+gap, not space-y: space-y sets margin-top, which the .v2
              button reset zeroes on these <button> cards (same issue
              BillingCycleToggle.tsx works around). gap isn't a margin, so
              it isn't affected. */}
          <div className="flex flex-col gap-3">
            <RadioOptionCard
              selected={formik.values.send}
              onClick={() => {
                formik.setFieldValue("send", true);
                formik.setFieldValue("date", dayjs().format("YYYY-MM-DD"));
              }}
              icon={<Send className="size-6 text-action" />}
              title="Send the gift right now"
              description="The recipient will receive an email with the gift details within minutes."
            />
            <RadioOptionCard
              selected={!formik.values.send}
              onClick={() => formik.setFieldValue("send", false)}
              icon={<Gift className="size-6 text-action" />}
              title="Schedule delivery"
              description="Choose a date for the gift to be delivered."
            />
          </div>

          {!formik.values.send && (
            <div className="mt-4 p-4 rounded-xl bg-muted border border-border">
              <label className="text-xs font-semibold mb-1.5 block text-foreground">
                Delivery date
              </label>
              <div className="relative">
                <CalendarIcon className="size-4 text-muted-foreground absolute left-3 top-2.5" />
                <Input
                  type="date"
                  min={dayjs().format("YYYY-MM-DD")}
                  className="pl-9 bg-card"
                  value={formik.values.date}
                  onChange={(e) => formik.setFieldValue("date", e.target.value)}
                />
              </div>
              {formik.touched.date && formik.errors.date && (
                <p className="text-xs text-destructive mt-1 font-medium">
                  {formik.errors.date}
                </p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
